import { useCallback } from 'react';
import { I18n } from 'react-redux-i18n';
import { Button, Flex } from 'antd';

import Icon from 'components/Icon';
import MultiSelect from '../MultiSelect';
import Select from '../Select';
import SliderPriceRange from '../SliderPriceRange';
import { isMobile } from 'react-device-detect';

import { useDistributionGlobalDataFilters } from 'apiTyped/hooks/global_data_view.query';
import { FiltersTypes } from 'interface/Distribution/globalView';
import toOptionsList from 'utils/valuesToOptions';
import NewCard from '../Card';

type GlobalViewFiltersProps = {
  stateFilters: FiltersTypes;
  currency: string;
  dnForNetwork: number;
  spiritGeographyFiltersEnabled: boolean;
  // eslint-disable-next-line no-unused-vars
  onChangeFilters: (e: any) => void;
  onClearFilters: () => void;
};

const GlobalViewFilters = ({
  stateFilters,
  currency,
  dnForNetwork,
  spiritGeographyFiltersEnabled,
  onChangeFilters,
  onClearFilters
}: GlobalViewFiltersProps) => {
  const { filters } = useDistributionGlobalDataFilters({
    network: stateFilters.network as 'on' | 'off',
    premium_store: false,
    survey_year:
      stateFilters.surveDate === 'flow' ? 'current' : stateFilters.surveDate
  });

  const setSpiritFormatGroup = useCallback((spiritFormatGroup: string) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      spiritFormatGroup
    }));
  }, []);

  const setNetwork = useCallback((network: string) => {
    onChangeFilters((prevState: FiltersTypes) => ({ ...prevState, network }));
  }, []);

  const setPosStatus = useCallback((posStatus: string) => {
    onChangeFilters((prevState: FiltersTypes) => ({ ...prevState, posStatus }));
  }, []);

  // const setZone = useCallback((zone: string) => {
  //   onChangeFilters((prevState: FiltersTypes) => ({ ...prevState, zone }));
  // }, []);

  // const setCountry = useCallback((country: string) => {
  //   onChangeFilters((prevState: FiltersTypes) => ({ ...prevState, country }));
  // }, []);

  const setMarket = useCallback((markets: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({ ...prevState, markets }));
  }, []);

  // const setSurveDate = useCallback((surveDate: string) => {
  //   onChangeFilters((prevState: FiltersTypes) => ({ ...prevState, surveDate }));
  // }, []);

  const setWineGrapeVariety = useCallback((wineGrapeVariety: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      wineGrapeVariety
    }));
  }, []);

  const setWineFormatGroup = useCallback((wineFormatGroup: string) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      wineFormatGroup
    }));
  }, []);

  const setWineColor = useCallback((wineColor: string) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      wineColor: wineColor
    }));
  }, []);

  const setWineSubType = useCallback((wineSubType: string | number) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      wineSubType: wineSubType.toString(),
      wineCountry: 'all',
      wineRegion: 'all'
    }));
  }, []);

  const setAppelation = useCallback((wineAppelation: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      wineAppelation
    }));
  }, []);

  const setWineCountry = useCallback((wineCountry: string) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      wineCountry,
      wineRegion: 'all'
    }));
  }, []);

  const setWineRegion = useCallback((wineRegion: string) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      wineRegion
    }));
  }, []);

  const setRangePrices = useCallback((rangePrices: number[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      rangePrices
    }));
  }, []);

  const setStorePriceBand = useCallback((storePriceBand: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      storePriceBand
    }));
  }, []);

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  const setStoreAwards = useCallback((storeAwards: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      storeAwards
    }));
  }, []);

  const setStoreWsAward = useCallback((storeWsAward: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      storeWsAward
    }));
  }, []);

  const setStoreMichelinStar = useCallback((storeMichelinStar: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      storeMichelinStar
    }));
  }, []);

  const setVintage = useCallback((vintage: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      vintage
    }));
  }, []);

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  const setStoreFacingBand = useCallback((storeFacingBand: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      storeFacingBand
    }));
  }, []);

  const setStorePrimaryType = useCallback((storePrimaryType: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      storePrimaryType
    }));
  }, []);

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  const setStoreRating = useCallback((storeRating: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      storeRating
    }));
  }, []);

  const setClientRefs = useCallback((clientRefs: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      clientRefs
    }));
  }, []);

  const setClientCustomRefs = useCallback((clientCustomRefs: any[]) => {
    onChangeFilters((prevState: FiltersTypes) => ({
      ...prevState,
      clientCustomRefs,
      // Auto-switch to active when a brand is selected, reset to all when cleared
      posStatus: clientCustomRefs.length > 0 ? 'pos' : 'all'
    }));
  }, []);

  const defaultOption = { value: 'all', label: I18n.t('placeholderAll') };
  const networkOptions = [
    { value: 'on', label: I18n.t('restaurantsMaj') }
    // { value: 'off', label: I18n.t('wineShopsMaj') }
  ];

  const defaultZoneOption = { value: 'world', label: I18n.t('world') };
  const zoneOptions = toOptionsList(filters.zones, 'name', 'id');

  type Option = {
    label: string;
    value: string;
    parent_id?: string;
  };

  type SpiritCountry = {
    id: string | number;
    name: string;
    wine_sub_type_id: string | number;
  };

  function getItemsForZone(selectedZone: string = 'world', items: any) {
    const allOption: Option = { value: 'all', label: I18n.t('placeholderAll') };
    if (!selectedZone || !items || items.length === 0) {
      return [allOption];
    }
    const itemsOptions = items.map((item: any) => ({
      value: item.id,
      label: item.name,
      parent_id: item.continent_id ? item.continent_id.toString() : undefined
    }));
    const filteredOptions =
      selectedZone === 'world'
        ? itemsOptions
        : itemsOptions.filter(
            (item: Option) => item.parent_id === selectedZone.toString()
          );
    if (filteredOptions.length === 0) {
      return [allOption];
    }
    const sortedOptions = filteredOptions.sort((a: Option, b: Option) =>
      a.label.localeCompare(b.label)
    );
    return [allOption, ...sortedOptions];
  }

  const marketsOptions = getItemsForZone(
    stateFilters.zone,
    filters.markets.filter(
      (market) => String(market.country_id) === stateFilters.country
    )
  );

  const wineAppelationOptions = [
    defaultOption,
    ...toOptionsList(filters.wine_appelations, 'name', 'id')
  ];

  const wineSubTypeOptions = [
    defaultOption,
    ...toOptionsList(filters.wine_sub_types, 'name', 'id')
  ];

  zoneOptions.unshift(defaultZoneOption);

  // const surveyOptions = [
  //   {
  //     value: 'flow',
  //     label: I18n.t('globalView.filters.options.surveyDate.flow')
  //   },
  //   // @ts-expect-error toOptionsList is js options
  //   ...toOptionsList(filters.surveys)
  // ];
  const colorsOptions = [
    defaultOption,
    {
      value: '1',
      label: I18n.t('globalView.filters.options.colorsWine.red')
    },
    {
      value: '5',
      label: I18n.t('globalView.filters.options.colorsWine.sparkling')
    },
    {
      value: '3',
      label: I18n.t('globalView.filters.options.colorsWine.sweet')
    },
    {
      value: '4',
      label: I18n.t('globalView.filters.options.colorsWine.white')
    },
    {
      value: '2',
      label: I18n.t('globalView.filters.options.colorsWine.rose')
    },
    {
      value: '6',
      label: I18n.t('globalView.filters.options.colorsWine.fortified')
    }
  ];

  const wineGrapeVarietyOptions = [
    defaultOption,
    ...toOptionsList(filters.wine_grape_varieties, 'name', 'id')
  ];

  const defaultSizeOption = {
    ...defaultOption,
    label: I18n.t('globalView.filters.formatOptions.allSizes')
  };

  // API returns the raw wine_format_group.name slug; label mapping lives here
  // (via i18n) rather than server-side so it stays translatable in the frontend.
  const WINE_FORMAT_LABELS: Record<string, string> = {
    glass: I18n.t('globalView.filters.formatOptions.glass'),
    bottle: I18n.t('globalView.filters.formatOptions.bottle'),
    large: I18n.t('globalView.filters.formatOptions.large'),
    half: I18n.t('globalView.filters.formatOptions.half')
  };

  const spiritFormatGroupOptions = [
    defaultSizeOption,
    ...toOptionsList(filters.spirit_format_groups, 'name', 'id')
  ];

  const wineFormatGroupOptions = [
    defaultSizeOption,
    ...toOptionsList(filters.wine_format_groups, 'name', 'id').map(
      (option) => ({
        ...option,
        label: WINE_FORMAT_LABELS[option.label?.toLowerCase()] ?? option.label
      })
    )
  ];

  // const appelationsOptions = [
  //   defaultOption,
  //   // @ts-expect-error toOptionsList is js options
  //   ...toOptionsList(filters.wine_appelations, 'name')
  // ];

  const wineCountriesOptions = [
    defaultOption,
    ...toOptionsList(filters.wine_countries, 'name', 'id')
  ];

  const spiritCountries = filters.spirit_countries as SpiritCountry[];
  const spiritCountriesBySubType =
    stateFilters.wineSubType === 'all'
      ? spiritCountries
      : spiritCountries.filter(
          (country) =>
            country.wine_sub_type_id.toString() ===
            stateFilters.wineSubType.toString()
        );
  const uniqueSpiritCountries = Array.from(
    new Map(
      spiritCountriesBySubType.map((country) => [country.id, country])
    ).values()
  );
  const spiritCountriesOptions = [
    defaultOption,
    ...toOptionsList(uniqueSpiritCountries, 'name', 'id')
  ];

  function getWineRegions(
    selectedWineCountry: string = 'all',
    wineRegions: any
  ) {
    const allOption: Option = { value: 'all', label: I18n.t('placeholderAll') };
    if (
      !selectedWineCountry ||
      selectedWineCountry === 'all' ||
      !wineRegions ||
      wineRegions.length === 0
    ) {
      return [allOption];
    }
    const itemsOptions = wineRegions.map((item: any) => ({
      value: item.id,
      label: item.name,
      parent_id: item.wine_country_id
        ? item.wine_country_id.toString()
        : undefined
    }));
    const filteredOptions = itemsOptions.filter(
      (item: Option) => item.parent_id === selectedWineCountry.toString()
    );
    if (filteredOptions.length === 0) {
      return [allOption];
    }
    const sortedOptions = filteredOptions.sort((a: Option, b: Option) =>
      a.label.localeCompare(b.label)
    );
    return [allOption, ...sortedOptions];
  }

  // Only set the wine region if the country is selected
  // This is to avoid showing regions when no country is selected
  const wineRegionsOptions = getWineRegions(
    stateFilters.wineCountry,
    filters.wine_regions
  );

  const selectedNetword = networkOptions.find(
    (option) => option.value === stateFilters.network
  );

  const defaultOptionWithIcon = {
    value: 'all',
    label: I18n.t('placeholderAll'),
    icon: undefined
  };

  const storePriceBandOptions = toOptionsList(
    filters.store_price_bands || [],
    'name',
    'id'
  );
  storePriceBandOptions.unshift(defaultOption);
  const storeFacingBandOptions = toOptionsList(
    filters.store_facing_bands || [],
    'name',
    'id'
  );
  storeFacingBandOptions.unshift(defaultOption);
  const storeAwardsOptions = (filters.store_awards || []).map((award: any) => ({
    value: award.id,
    label: award.name,
    icon: award.icon
  }));
  storeAwardsOptions.unshift(defaultOptionWithIcon);
  const storePrimaryTypeOptions = toOptionsList(
    filters.store_primary_types || [],
    'name',
    'id'
  );
  storePrimaryTypeOptions.unshift(defaultOption);
  const storeRatingOptions = (filters.store_ratings || []).map(
    (rating: any) => ({
      value: rating.id,
      label: rating.name,
      icon: rating.icon
    })
  );
  storeRatingOptions.unshift(defaultOptionWithIcon);

  const wsAwardOptions = (filters.ws_award_options || []).map((opt: any) => ({
    value: opt.value,
    label: opt.label
  }));

  const michelinStarOptions = (filters.michelin_star_options || []).map(
    (opt: any) => ({
      value: opt.value,
      label: opt.label
    })
  );

  const vintageOptions = (filters.vintage_options || []).map((opt: any) => ({
    value: opt.value,
    label: opt.label
  }));

  const clientRefsOptions = toOptionsList(filters.client_refs, 'name', 'id');
  const clientCustomRefsOptions = toOptionsList(
    filters.client_custom_refs,
    'name',
    'id'
  );

  const posStatusOptions = [
    ...(stateFilters.clientCustomRefs.length === 0
      ? [
          {
            value: 'all',
            label: I18n.t('restaurants&caves.pos.typeOptions.all')
          }
        ]
      : []),
    {
      value: 'pos',
      label: I18n.t('restaurants&caves.pos.typeOptions.pos')
    },
    {
      value: 'conquer',
      label: I18n.t('restaurants&caves.pos.typeOptions.conquer')
    },
    {
      value: 'lost',
      label: I18n.t('restaurants&caves.pos.typeOptions.lost')
    }
  ];

  return (
    <NewCard
      header={
        <div className="header-card-title">
          {`${selectedNetword?.label} - ${dnForNetwork}`}
        </div>
      }
      actions={
        <Button
          type="text"
          size="middle"
          icon={<Icon icon="clearFilters" alt="clear-filters" />}
          className="button-clear-filters"
          onClick={onClearFilters}
        >
          {!isMobile && I18n.t('globalView.filters.buttonClear')}
        </Button>
      }
    >
      <Flex justify="space-evenly" align="center" gap="large" vertical>
        {/* Wine Filters Row */}
        <div className="filtersContainer">
          <MultiSelect
            label={I18n.t('globalView.filters.labels.market')}
            selectedOptions={stateFilters.markets}
            options={marketsOptions}
            onChange={setMarket}
            showSelectionCount={true}
          />
          {stateFilters.wineType === '1' ? (
            <Select
              label={I18n.t('globalView.filters.labels.color')}
              value={colorsOptions.find(
                (item) => item.value === stateFilters.wineColor
              )}
              options={colorsOptions}
              onChange={(e: any) => setWineColor(e.value)}
              isClearable={false}
            />
          ) : (
            stateFilters.wineType === '2' && (
              <Select
                label={I18n.t('globalView.filters.labels.spiritType')}
                value={wineSubTypeOptions.find(
                  (item) => item.value === stateFilters.wineSubType
                )}
                options={wineSubTypeOptions}
                onChange={(e: any) => setWineSubType(e.value)}
                isClearable={false}
              />
            )
          )}
          {stateFilters.wineType === '2' && spiritGeographyFiltersEnabled && (
            <Select
              label={I18n.t('globalView.filters.labels.spiritCountry')}
              value={spiritCountriesOptions.find(
                (item) => item.value === stateFilters.wineCountry
              )}
              options={spiritCountriesOptions}
              onChange={(e: any) => setWineCountry(e.value)}
              isClearable={false}
            />
          )}
          {stateFilters.wineType === '2' && spiritGeographyFiltersEnabled && (
            <Select
              label={I18n.t('globalView.filters.labels.spiritRegion')}
              value={wineRegionsOptions.find(
                (item) => item.value === stateFilters.wineRegion
              )}
              options={wineRegionsOptions}
              onChange={(e: any) => setWineRegion(e.value)}
              isClearable={false}
            />
          )}
          {stateFilters.wineType === '2' && (
            <Select
              label={I18n.t('globalView.filters.labels.spiritFormatGroup')}
              value={spiritFormatGroupOptions.find(
                (item) => item.value === stateFilters.spiritFormatGroup
              )}
              options={spiritFormatGroupOptions}
              onChange={(e: any) => setSpiritFormatGroup(e.value)}
              isClearable={false}
            />
          )}
          {stateFilters.wineType === '1' && (
            <MultiSelect
              label={I18n.t('globalView.filters.labels.grapeVariety')}
              selectedOptions={stateFilters.wineGrapeVariety}
              options={wineGrapeVarietyOptions}
              onChange={setWineGrapeVariety}
              showSelectionCount={true}
            />
          )}
          {stateFilters.wineType === '1' && (
            <Select
              label={I18n.t('globalView.filters.labels.wineCountry')}
              value={wineCountriesOptions.find(
                (item) => item.value === stateFilters.wineCountry
              )}
              options={wineCountriesOptions}
              onChange={(e: any) => setWineCountry(e.value)}
              isClearable={false}
            />
          )}
          {stateFilters.wineType === '1' && (
            <Select
              label={I18n.t('globalView.filters.labels.wineRegion')}
              value={wineRegionsOptions.find(
                (item) => item.value === stateFilters.wineRegion
              )}
              options={wineRegionsOptions}
              onChange={(e: any) => setWineRegion(e.value)}
              isClearable={false}
            />
          )}
          {stateFilters.wineType === '1' && (
            <Select
              label={I18n.t('globalView.filters.labels.wineFormatGroup')}
              value={wineFormatGroupOptions.find(
                (item) => item.value === stateFilters.wineFormatGroup
              )}
              options={wineFormatGroupOptions}
              onChange={(e: any) => setWineFormatGroup(e.value)}
              isClearable={false}
            />
          )}
          {stateFilters.wineType === '1' && (
            <MultiSelect
              label={I18n.t('globalView.filters.labels.appelations')}
              selectedOptions={stateFilters.wineAppelation}
              options={wineAppelationOptions}
              onChange={setAppelation}
              showSelectionCount={true}
            />
          )}
          {(!clientCustomRefsOptions ||
            clientCustomRefsOptions.length === 0) && (
            <MultiSelect
              label={I18n.t('globalView.filters.labels.clientRefs')}
              selectedOptions={stateFilters.clientRefs}
              options={clientRefsOptions}
              onChange={setClientRefs}
              showSelectionCount={true}
            />
          )}
          {clientCustomRefsOptions &&
            clientCustomRefsOptions.length > 0 &&
            (stateFilters.tableTypeActive === 'point_of_sale' ||
              stateFilters.tableTypeActive === 'wine') && (
              <MultiSelect
                label={I18n.t('globalView.filters.labels.clientCustomRefs')}
                selectedOptions={stateFilters.clientCustomRefs}
                options={clientCustomRefsOptions}
                onChange={setClientCustomRefs}
                showSelectionCount={true}
              />
            )}

          {/* <div
            style={{
              minWidth: '12%'
            }}
          >
            <Select
              label={I18n.t('globalView.filters.labels.network')}
              value={networkOptions.find(
                (option) => option.value === stateFilters.network
              )}
              options={networkOptions}
              onChange={(e: any) => setNetwork(e.value)}
              isClearable={false}
            />
          </div> */}

          <MultiSelect
            label={I18n.t(
              'globalView.filters.labels.storePriceBand',
              'Price Band'
            )}
            selectedOptions={stateFilters.storePriceBand}
            options={storePriceBandOptions}
            onChange={setStorePriceBand}
            showSelectionCount={true}
          />

          <MultiSelect
            label={I18n.t(
              'globalView.filters.labels.storePrimaryType',
              'Cuisine'
            )}
            selectedOptions={stateFilters.storePrimaryType}
            options={storePrimaryTypeOptions}
            onChange={setStorePrimaryType}
            showSelectionCount={true}
          />

          {/* <MultiSelect
            label={I18n.t(
              'globalView.filters.labels.storeFacingBand',
              'Facing Band'
            )}
            selectedOptions={stateFilters.storeFacingBand}
            options={storeFacingBandOptions}
            onChange={setStoreFacingBand}
            showSelectionCount={true}
          /> */}
          {/* <MultiSelect
            label={I18n.t('globalView.filters.labels.storeAwards', 'Awards')}
            selectedOptions={stateFilters.storeAwards}
            options={storeAwardsOptions}
            onChange={setStoreAwards}
            renderOptionIcon={renderOptionIcon}
            showSelectionCount={true}
          /> */}
          {/* <MultiSelect
            label={I18n.t('globalView.filters.labels.storeRating', 'Rating')}
            selectedOptions={stateFilters.storeRating}
            options={storeRatingOptions}
            onChange={setStoreRating}
            renderOptionIcon={renderOptionIcon}
            showSelectionCount={true}
          /> */}
          <MultiSelect
            label={I18n.t('globalView.filters.labels.storeWsAward', 'WS Award')}
            selectedOptions={stateFilters.storeWsAward}
            options={wsAwardOptions}
            onChange={setStoreWsAward}
            showSelectionCount={true}
          />
          <MultiSelect
            label={I18n.t(
              'globalView.filters.labels.storeMichelinStar',
              'Michelin Stars'
            )}
            selectedOptions={stateFilters.storeMichelinStar}
            options={michelinStarOptions}
            onChange={setStoreMichelinStar}
            showSelectionCount={true}
          />
          {stateFilters.tableTypeActive === 'wine' && (
            <MultiSelect
              label={I18n.t('globalView.filters.labels.vintage', 'Vintage')}
              selectedOptions={stateFilters.vintage}
              options={vintageOptions}
              onChange={setVintage}
              showSelectionCount={true}
            />
          )}
          {stateFilters.tableTypeActive === 'point_of_sale' && (
            <Select
              label={I18n.t('globalView.filters.labels.posStatus')}
              value={posStatusOptions.find(
                (option) => option.value === stateFilters.posStatus
              )}
              options={posStatusOptions}
              onChange={(e: any) => setPosStatus(e.value)}
              isClearable={false}
            />
          )}
        </div>

        {stateFilters.tableTypeActive !== 'positioning' && (
          <div className="filtersContainer filtersContainerSlideRange">
            <SliderPriceRange
              range={stateFilters.rangePrices}
              setRange={setRangePrices}
              currency={currency}
              label={I18n.t(
                `globalView.filters.labels.priceRange.${stateFilters?.tableTypeActive}`
              )?.toUpperCase()}
            />
          </div>
        )}
        {/* Clear Filters Button */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '16px'
          }}
        ></div>
      </Flex>
    </NewCard>
  );
};

export default GlobalViewFilters;
